import { Block, Flex, Icon, ProductColor, Typography } from '@aloft/toolkit-sui';
import { Progress } from '@aloft/toolkit-sui/chakra';

import { useCallback } from 'react';
import { Accept, useDropzone } from 'react-dropzone';

import { FileUploaderFileItem } from './FileUploaderFileItem.tsx';

const getBorderColor = (
  isDisabled: boolean,
  isDragAccept: boolean,
  isDragReject: boolean,
): ProductColor => {
  if (isDisabled) return 'strokeDefault';
  if (isDragAccept) return 'buttonActive';
  if (isDragReject) return 'error';
  return 'buttonDefault';
};

type FileUploaderFile = {
  name: string;
  id: string;
  blobID?: string;
  isError?: boolean;
  isSuccess?: boolean;
  previewUrl?: string;
};

type FileUploaderProps = {
  files: FileUploaderFile[];
  onFileAdd: (file: File, result: string, isError?: boolean) => void;
  onFileDelete?: (id: string) => void;
  onFileDownload?: (id: string) => void;
  isLoading?: boolean;
  loadingMessage?: string;
  isDisabled?: boolean;
  maxFiles?: number;
  dropzoneMessage?: string;
  showProgressBar?: boolean;
  accept?: Accept;
  error?: string;
  warn?: string;
  id?: string;
  readAs?: 'text' | 'dataURL' | 'arrayBuffer' | 'binaryString' | 'none';
};

export const FileUploader = ({
  files,
  isDisabled = false,
  isLoading = false,
  loadingMessage = 'Uploading',
  maxFiles = 1,
  dropzoneMessage = 'Click to browse, or drag and drop',
  showProgressBar = false,
  accept,
  error,
  warn,
  id,
  onFileAdd,
  onFileDelete,
  onFileDownload,
  readAs = 'text',
}: FileUploaderProps) => {
  const onDrop = useCallback(
    ([file]: File[]) => {
      if (!file) return;

      if (readAs === 'none') {
        onFileAdd(file, '', false);
        return;
      }

      const onLoadEnd = (loadedFile: File, ev: ProgressEvent<FileReader>) => {
        // Silly but necessary to narrow the type
        if (typeof ev.target?.result !== 'string') throw new Error('Unexpected file read result');
        onFileAdd(loadedFile, ev.target.result);
      };

      const reader = new FileReader();
      reader.addEventListener('loadend', (e) => onLoadEnd(file, e));
      reader.addEventListener('error', console.error);

      switch (readAs) {
        case 'dataURL':
          reader.readAsDataURL(file);
          break;
        case 'arrayBuffer':
          reader.readAsArrayBuffer(file);
          break;
        case 'text':
        default:
          reader.readAsText(file);
          break;
      }
    },
    [onFileAdd, readAs],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject, isDragActive } = useDropzone({
    onDrop,
    disabled: isDisabled,
    maxFiles,
    accept,
  });

  const borderColor = getBorderColor(isDisabled, isDragAccept, isDragReject);

  return (
    <Flex height="100%" width="100%" flexDirection="column" gap={1}>
      <Block
        flexGrow={1}
        borderColor={borderColor}
        borderStyle="dashed"
        borderWidth={isDragActive ? '2px' : '1px'}
        backgroundColor={isDisabled ? 'disabledFill' : 'selected1'}
        borderRadius="6px"
        cursor={isDisabled || isLoading ? 'not-allowed' : 'pointer'}
      >
        {isLoading && showProgressBar ? (
          <Flex
            flexDirection="column"
            height="100%"
            width="100%"
            alignItems="center"
            justifyContent="center"
            gap={4}
          >
            <Progress isIndeterminate width="100%" />
            <Typography variant="h4" color="textSecondary">
              {loadingMessage}
            </Typography>
          </Flex>
        ) : (
          <Block {...getRootProps()} height="100%" width="100%">
            <input {...getInputProps()} id={id} />
            <Flex
              width="100%"
              height="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              gap={4}
            >
              {files.length < maxFiles ? (
                <>
                  <Icon
                    icon="cloud-upload"
                    size="xxl"
                    color="buttonDefault"
                    __styleDoNotUse={{ opacity: 0.3 }}
                  />
                  <Typography variant="h4">{dropzoneMessage}</Typography>
                </>
              ) : null}
              {files.map((f) => (
                <FileUploaderFileItem
                  key={f.id}
                  blobID={f.blobID}
                  filename={f.name}
                  previewUrl={f.previewUrl}
                  {...(onFileDownload && {
                    onDownloadClick: () => onFileDownload(f.id),
                  })}
                  {...(onFileDelete && {
                    onDeleteClick: () => onFileDelete(f.id),
                  })}
                  isError={f.isError}
                  isDisabled={isDisabled}
                  isSuccess={f.isSuccess}
                />
              ))}
            </Flex>
          </Block>
        )}
      </Block>
      {error && (
        <Typography variant="body2" color="textError">
          {error}
        </Typography>
      )}
      {warn && <Typography variant="body2">{warn}</Typography>}
    </Flex>
  );
};
